import { useState } from 'react';
import { calculateDose, formatFrequency, formatCategoryName } from '../../util/RecUtil';
import { resolveProductDetails, getSupplementData, getProductName } from '../../util/ProtocolUtil';

function DesktopProtocolRow({
    category,
    categoryWeight,
    products,
    mode,
    priority,
    selectedProduct,
    onSelectProduct,
    highlighted,
    doseEdit,
    onDoseEdit,
    doctorViewMode,
    readOnly = false,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    isDraggedOver,
    onRemove,
    complianceMode = false,
    complianceChecked = false,
    onComplianceToggle,
    weeklyProgress,
    entryType = 'supplement',
    locked = false,
    lockedByName = null,
}) {
    const isIntervention = entryType === 'intervention';
    const [expanded, setExpanded] = useState(false);

    // Find the active product
    const activeProduct = products.find(p => {
        return getProductName(p) === selectedProduct;
    }) || products[0];

    const productName = getProductName(activeProduct);

    const details = resolveProductDetails(activeProduct);
    const defaultDose = calculateDose(details, activeProduct?.total_weight || 0, mode);
    const catalogEntry = getSupplementData(productName);
    const contraindications = catalogEntry?.contraindications || [];

    // Current dose values (edits override defaults)
    const currentDose = doseEdit?.dose ?? defaultDose?.dose ?? 1;
    const currentFrequency = doseEdit?.frequency ?? details._entryFrequency ?? defaultDose?.frequency ?? 1;
    const inputDose = doseEdit && doseEdit.dose === null ? '' : currentDose;
    const currentDailyTotal = currentDose * currentFrequency;
    const currentEmptyStomach = doseEdit?.emptyStomach ?? details.empty_stomach ?? null;
    const currentTimeOfDay = doseEdit?.timeOfDay ?? null;
    const currentInstructions = doseEdit?.instructions ?? null;
    const unit = details.unit || 'capsule';

    // Min/max dose range
    const minDose = details.min_daily_dose || 1;
    const maxDose = mode === 'therapeutic'
        ? (details.max_therapeutic_daily_dose || details.max_support_daily_dose || minDose)
        : (details.max_support_daily_dose || minDose);

    const handleDoseChange = (field, value) => {
        const prev = doseEdit || {
            dose: currentDose,
            frequency: currentFrequency,
        };
        const updated = { ...prev, [field]: value };
        onDoseEdit?.(category, updated);
    };

    const showEditControls = !readOnly && !complianceMode;

    return (
        <div
            className={`protocol-row ${highlighted ? 'highlighted' : ''} ${isDraggedOver ? 'drag-over' : ''} ${complianceMode && complianceChecked ? 'protocol-row--checked' : ''} ${complianceMode && onComplianceToggle ? 'protocol-row--compliance' : ''}`}
            draggable={showEditControls}
            onDragStart={showEditControls ? onDragStart : undefined}
            onDragOver={showEditControls ? onDragOver : undefined}
            onDrop={showEditControls ? onDrop : undefined}
            onDragEnd={showEditControls ? onDragEnd : undefined}
            onClick={complianceMode && onComplianceToggle ? () => onComplianceToggle(productName) : undefined}
        >
            <div className="protocol-row-info">
                {complianceMode ? (
                    <span
                        role={onComplianceToggle ? 'button' : undefined}
                        className={`protocol-row-compliance-check ${complianceChecked ? 'protocol-row-compliance-check--on' : ''} ${!onComplianceToggle ? 'protocol-row-compliance-check--readonly' : ''}`}
                        onClick={onComplianceToggle ? (e) => { e.stopPropagation(); onComplianceToggle(productName); } : undefined}
                    >
                        {complianceChecked && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                            </svg>
                        )}
                    </span>
                ) : !readOnly ? (
                    <div className="protocol-row-drag-handle" title="Drag to reorder">
                        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                            <circle cx="3" cy="2" r="1.5" />
                            <circle cx="7" cy="2" r="1.5" />
                            <circle cx="3" cy="8" r="1.5" />
                            <circle cx="7" cy="8" r="1.5" />
                            <circle cx="3" cy="14" r="1.5" />
                            <circle cx="7" cy="14" r="1.5" />
                        </svg>
                    </div>
                ) : !complianceMode ? (
                    <div className="protocol-row-lock" title="Managed by your doctor">
                        <svg width="12" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                    </div>
                ) : null}
                <div className="protocol-row-rank">{priority}</div>
                <div className="protocol-row-details">
                    <div className="protocol-row-name">
                        {productName}
                        {locked && (
                            <span
                                className="protocol-entry-lock"
                                title={doctorViewMode
                                    ? 'Locked for the patient — only you can change this'
                                    : (lockedByName
                                        ? `Locked by Dr. ${lockedByName} — only your doctor can change this`
                                        : 'Locked by your doctor — only your doctor can change this')}
                            >
                                <svg width="11" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0110 0v4" />
                                </svg>
                            </span>
                        )}
                        {complianceMode && (
                            <span className={`protocol-row-type-tag protocol-row-type-tag--${isIntervention ? 'activity' : 'supplement'}`}>
                                {isIntervention ? 'activity' : 'supplement'}
                            </span>
                        )}
                    </div>
                    <div className="protocol-row-dose">
                        {details.sets && (
                            <>{details.sets} sets &times; </>
                        )}
                        {currentDose} {unit} {formatFrequency(currentFrequency, details.freq_unit)}
                        {weeklyProgress && (
                            <>
                                <span className="protocol-row-sep">&middot;</span>
                                <span className="protocol-row-weekly-progress">
                                    {Array.from({ length: weeklyProgress.target }, (_, i) => (
                                        <span key={i} className={`weekly-dot ${i < weeklyProgress.done ? 'weekly-dot--filled' : ''}`} />
                                    ))}
                                    <span className="weekly-fraction">{weeklyProgress.done}/{weeklyProgress.target}</span>
                                </span>
                            </>
                        )}
                        {!isIntervention && currentEmptyStomach !== null && (
                            <>
                                <span className="protocol-row-sep">&middot;</span>
                                <span className="protocol-row-timing">
                                    {currentEmptyStomach ? 'Empty stomach' : 'With food'}
                                </span>
                            </>
                        )}
                        {currentTimeOfDay && (
                            <>
                                <span className="protocol-row-sep">&middot;</span>
                                <span className="protocol-row-timing">
                                    {currentTimeOfDay === 'both' ? 'Morning & Before bed'
                                        : currentTimeOfDay === 'morning' ? 'Morning' : 'Before bed'}
                                </span>
                            </>
                        )}
                    </div>
                    {doctorViewMode && !isIntervention && (
                        <div className="protocol-row-dose-range">
                            Range: {minDose}&ndash;{maxDose} {unit}/day
                        </div>
                    )}
                    {contraindications.length > 0 && expanded && (
                        <div className="protocol-row-contraindication">
                            Contraindicated: {contraindications.join(', ')}
                        </div>
                    )}
                    {currentInstructions && !expanded && (
                        <div className="protocol-entry-instructions">{currentInstructions}</div>
                    )}
                    <div className="protocol-row-cats">
                        <span className="protocol-row-cat-tag">{formatCategoryName(category)}</span>
                    </div>
                </div>
                {expanded && (categoryWeight > 0 ? (
                    <div className="protocol-row-weight">
                        <div className="protocol-row-weight-bar">
                            <div
                                className="protocol-row-weight-fill"
                                style={{ width: `${Math.min(categoryWeight * 100, 100).toFixed(0)}%` }}
                            />
                        </div>
                        <span className="protocol-row-weight-label">
                            {(categoryWeight * 100).toFixed(0)}%
                        </span>
                    </div>
                ) : (
                    <div className="protocol-row-weight">
                        <span className="protocol-row-weight-manual">Manual</span>
                    </div>
                ))}
                {showEditControls && (
                    <button
                        className={`protocol-row-edit ${expanded ? 'active' : ''}`}
                        onClick={() => setExpanded(!expanded)}
                        title={expanded ? 'Collapse' : 'Edit'}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5z" />
                        </svg>
                    </button>
                )}
                {showEditControls && onRemove && (
                    <button
                        className="protocol-row-remove"
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        title="Remove supplement"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                )}
            </div>

            {expanded && showEditControls && (
                <>
                    <div className="protocol-row-edit-section">
                        <div className="editor-field">
                            <label>Dose</label>
                            <input
                                type="number"
                                min="1"
                                value={inputDose}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') {
                                        handleDoseChange('dose', null);
                                    } else {
                                        const parsed = parseInt(raw);
                                        if (!Number.isNaN(parsed)) handleDoseChange('dose', parsed);
                                    }
                                }}
                            />
                            <span>{unit}</span>
                        </div>
                        <span className="editor-field-sep">&times;</span>
                        <div className="editor-field">
                            <select
                                value={currentFrequency}
                                onChange={(e) => handleDoseChange('frequency', parseInt(e.target.value))}
                            >
                                {[1, 2, 3, 4].map(f => (
                                    <option key={f} value={f}>{f}x daily</option>
                                ))}
                            </select>
                        </div>
                        <span className="editor-field-result">
                            = {currentDailyTotal} {unit}/day total
                        </span>
                        {!isIntervention && (
                            <div className="editor-timing-group">
                                <label className="editor-timing-radio">
                                    <input
                                        type="radio"
                                        name={`food-${category}`}
                                        checked={doseEdit?.emptyStomach === false}
                                        onChange={() => {}}
                                        onClick={() => handleDoseChange('emptyStomach', doseEdit?.emptyStomach === false ? null : false)}
                                    />
                                    With food
                                </label>
                                <label className="editor-timing-radio">
                                    <input
                                        type="radio"
                                        name={`food-${category}`}
                                        checked={doseEdit?.emptyStomach === true}
                                        onChange={() => {}}
                                        onClick={() => handleDoseChange('emptyStomach', doseEdit?.emptyStomach === true ? null : true)}
                                    />
                                    Empty stomach
                                </label>
                            </div>
                        )}
                        <div className="editor-timing-group">
                            <label className="editor-timing-radio">
                                <input
                                    type="checkbox"
                                    checked={currentTimeOfDay === 'morning' || currentTimeOfDay === 'both'}
                                    onChange={() => {
                                        const hasMorning = currentTimeOfDay === 'morning' || currentTimeOfDay === 'both';
                                        const hasEvening = currentTimeOfDay === 'evening' || currentTimeOfDay === 'both';
                                        handleDoseChange('timeOfDay', hasMorning
                                            ? (hasEvening ? 'evening' : null)
                                            : (hasEvening ? 'both' : 'morning'));
                                    }}
                                />
                                Morning
                            </label>
                            <label className="editor-timing-radio">
                                <input
                                    type="checkbox"
                                    checked={currentTimeOfDay === 'evening' || currentTimeOfDay === 'both'}
                                    onChange={() => {
                                        const hasMorning = currentTimeOfDay === 'morning' || currentTimeOfDay === 'both';
                                        const hasEvening = currentTimeOfDay === 'evening' || currentTimeOfDay === 'both';
                                        handleDoseChange('timeOfDay', hasEvening
                                            ? (hasMorning ? 'morning' : null)
                                            : (hasMorning ? 'both' : 'evening'));
                                    }}
                                />
                                Before bed
                            </label>
                        </div>
                    </div>

                    {doctorViewMode && !isIntervention && (
                        <div className="protocol-row-dose-range-detail">
                            <span>Min recommended: {minDose} {unit}/day</span>
                            <span className="protocol-row-sep">&middot;</span>
                            <span>Max recommended: {maxDose} {unit}/day</span>
                        </div>
                    )}

                    {doctorViewMode && (
                        <div className="protocol-row-instructions">
                            <textarea
                                placeholder="Add instructions for patient..."
                                value={currentInstructions || ''}
                                onChange={(e) => handleDoseChange('instructions', e.target.value || null)}
                                rows={2}
                            />
                        </div>
                    )}

                    {products.length > 1 && (
                        <div className="protocol-row-tiles">
                            {products.map((product) => {
                                const pName = getProductName(product);
                                const pDetails = resolveProductDetails(product);
                                const pDose = calculateDose(pDetails, product.total_weight || 0, mode);
                                const isSelected = pName === selectedProduct
                                    || (!selectedProduct && product === products[0]);
                                return (
                                    <div
                                        key={pName}
                                        className={`protocol-row-tile ${isSelected ? 'selected' : ''}`}
                                        onClick={() => onSelectProduct(pName)}
                                    >
                                        {isSelected && <span className="tile-selected-badge">Selected</span>}
                                        <span className="tile-name">{pName}</span>
                                        <span className="tile-dose">
                                            {pDose?.dose || 1} {pDetails.unit || 'capsule'} {formatFrequency(pDose?.frequency || 1, pDetails.freq_unit || 'day')}
                                        </span>
                                        <span className="tile-timing">
                                            {pDetails.empty_stomach ? 'Empty stomach' : 'With food'}
                                        </span>
                                        <div className="tile-cats">
                                            {(product.fulfills_categories || []).map(fc => (
                                                <span key={fc.category} className="tile-cat-tag">
                                                    {formatCategoryName(fc.category)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default DesktopProtocolRow;
